import { Bell, LogOut, Moon, Sun } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';
import { useLogout, useSession } from '@/features/auth/session';
import { api } from '@/lib/api';
import { keys } from '@/lib/query';
import { useUiStore } from '@/stores/ui.store';
import { RunningTimer } from '@/features/pm/timers/RunningTimer';
import { IconButton } from '@/ui/Button';
import { Select } from '@/ui/Field';

export function Topbar() {
  const { t, i18n } = useTranslation();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const setLocale = useUiStore((s) => s.setLocale);
  const toggleNotifications = useUiStore((s) => s.toggleNotifications);
  const logout = useLogout();
  const { data: session } = useSession();

  const { data: unread } = useQuery({
    queryKey: keys.notificationCount,
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: config.pollIntervalMs,
    enabled: Boolean(session),
  });

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface px-6">
      <RunningTimer />

      <div className="ms-auto flex items-center gap-1">
        <Select
          value={i18n.language}
          onChange={(e) => setLocale(e.target.value)}
          aria-label={t('common.language')}
          className="h-9 w-auto px-2 text-xs font-medium text-ink-muted"
        >
          {config.supportedLocales.map((code) => (
            <option key={code} value={code}>{code.toUpperCase()}</option>
          ))}
        </Select>

        <IconButton
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          aria-label={t('common.theme')}
        >
          {theme === 'light'
            ? <Moon className="size-[18px]" aria-hidden />
            : <Sun className="size-[18px]" aria-hidden />}
        </IconButton>

        <IconButton onClick={() => toggleNotifications()} aria-label={t('nav.notifications')}>
          <Bell className="size-[18px]" aria-hidden />
          {!!unread?.count && (
            <span className="absolute end-1 top-1 min-w-4 rounded-full bg-danger px-1 text-[10px] font-bold leading-4 text-white">
              {unread.count}
            </span>
          )}
        </IconButton>

        <IconButton onClick={() => logout.mutate()} aria-label={t('auth.signOut')}>
          <LogOut className="size-[18px] rtl:rotate-180" aria-hidden />
        </IconButton>
      </div>
    </header>
  );
}
